// Local-only photo editing. No image pixels or source photos leave the browser
// until the administrator explicitly saves the reviewed, processed result.
export const CARD_RATIO = 63 / 88;
export const fullPhotoCorners = () => [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];

export function validCorners(points) {
  if (points?.length !== 4 || points.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y) || p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1)) return false;
  let area = 0;
  for (let i = 0; i < 4; i++) {
    const a = points[i], b = points[(i + 1) % 4], c = points[(i + 2) % 4];
    if ((b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x) <= 1e-6) return false;
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2 > .005;
}

// Project the output unit square into a source quadrilateral, then resample it.
// This is a perspective transform, not a bounding-box crop or bilinear warp.
export function squareToQuad(points) {
  if (!validCorners(points)) throw new Error('Place the four corners around the card without crossing the edges.');
  const [p0, p1, p2, p3] = points;
  const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y, dy3 = p0.y - p1.y + p2.y - p3.y;
  const determinant = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(determinant) < 1e-10) throw new Error('The selected card area is too narrow.');
  const g = (dx3 * dy2 - dx2 * dy3) / determinant;
  const h = (dx1 * dy3 - dx3 * dy1) / determinant;
  const a = p1.x - p0.x + g * p1.x, b = p3.x - p0.x + h * p3.x;
  const d = p1.y - p0.y + g * p1.y, e = p3.y - p0.y + h * p3.y;
  return (u, v) => {
    const divisor = g * u + h * v + 1;
    return { x: (a * u + b * v + p0.x) / divisor, y: (d * u + e * v + p0.y) / divisor };
  };
}

export function flattenPixels(source, width, height, points) {
  const project = squareToQuad(points);
  const output = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const point = project(x / Math.max(1, width - 1), y / Math.max(1, height - 1));
    const sx = Math.max(0, Math.min(source.width - 1, point.x * (source.width - 1)));
    const sy = Math.max(0, Math.min(source.height - 1, point.y * (source.height - 1)));
    const x0 = Math.floor(sx), y0 = Math.floor(sy), x1 = Math.min(source.width - 1, x0 + 1), y1 = Math.min(source.height - 1, y0 + 1);
    const fx = sx - x0, fy = sy - y0, target = (y * width + x) * 4;
    for (let channel = 0; channel < 3; channel++) {
      const a = source.data[(y0 * source.width + x0) * 4 + channel], b = source.data[(y0 * source.width + x1) * 4 + channel];
      const c = source.data[(y1 * source.width + x0) * 4 + channel], d = source.data[(y1 * source.width + x1) * 4 + channel];
      output[target + channel] = (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
    }
    output[target + 3] = 255;
  }
  return output;
}

export function adjustLight(pixels, brightness = 0, contrast = 0) {
  const output = new Uint8ClampedArray(pixels);
  const gain = 1 + Math.max(-20, Math.min(20, contrast)) / 100;
  const offset = Math.max(-20, Math.min(20, brightness)) * 2.55;
  for (let i = 0; i < output.length; i += 4) for (let c = 0; c < 3; c++) output[i + c] = (pixels[i + c] - 127.5) * gain + 127.5 + offset;
  return output;
}

export function gentleLight(pixels) {
  const histogram = new Uint32Array(256);
  const total = pixels.length / 4;
  if (!total) return { brightness: 0, contrast: 0 };
  for (let i = 0; i < pixels.length; i += 4) histogram[Math.round(.2126 * pixels[i] + .7152 * pixels[i + 1] + .0722 * pixels[i + 2])]++;
  let sum = 0, low = 0, high = 255;
  for (let i = 0; i < 256; i++) { sum += histogram[i]; if (sum >= total * .05) { low = i; break; } }
  sum = 0;
  for (let i = 0; i < 256; i++) { sum += histogram[i]; if (sum >= total * .95) { high = i; break; } }
  return { brightness: Math.round(Math.max(-10, Math.min(10, (127.5 - (low + high) / 2) / 2.55))),
    contrast: Math.round(Math.max(-10, Math.min(12, (200 / Math.max(1, high - low) - 1) * 100))) };
}

function canvas(width, height) {
  const result = document.createElement('canvas'); result.width = width; result.height = height;
  return result;
}

export async function readPhoto(file) {
  if (file.size > 10 * 1024 * 1024) throw new Error('Images must be 10 MB or smaller');
  const header = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  const jpeg = header[0] === 255 && header[1] === 216 && header[2] === 255;
  const png = [137, 80, 78, 71, 13, 10, 26, 10].every((value, i) => header[i] === value);
  const webp = String.fromCharCode(...header.slice(0, 4)) === 'RIFF' && String.fromCharCode(...header.slice(8, 12)) === 'WEBP';
  if (!jpeg && !png && !webp) throw new Error('Choose a JPEG, PNG or WebP card photo');
  if (webp && String.fromCharCode(...header.slice(12, 16)) === 'VP8X' && (header[20] & 2)) throw new Error('Animated images are not supported');
  if (png) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const view = new DataView(bytes.buffer);
    for (let offset = 8; offset + 12 <= bytes.length;) {
      if (String.fromCharCode(...bytes.slice(offset + 4, offset + 8)) === 'acTL') throw new Error('Animated images are not supported');
      offset += view.getUint32(offset) + 12;
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('This image could not be opened. Choose another photo.'));
      element.src = url;
    });
    if (image.naturalWidth * image.naturalHeight > 20_000_000 || Math.min(image.naturalWidth, image.naturalHeight) < 64 || Math.max(image.naturalWidth, image.naturalHeight) > 10000) throw new Error('Use an image at least 64 pixels per side and no more than 20 megapixels');
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
    const result = canvas(Math.round(image.naturalWidth * scale), Math.round(image.naturalHeight * scale));
    const context = result.getContext('2d');
    context.fillStyle = '#fff'; context.fillRect(0, 0, result.width, result.height);
    // Modern browsers apply EXIF orientation when decoding the local image.
    context.drawImage(image, 0, 0, result.width, result.height);
    return result;
  } finally { URL.revokeObjectURL(url); }
}

export function rotatePhoto(source, turns) {
  const angle = ((turns % 4) + 4) % 4;
  if (!angle) return source;
  const result = canvas(angle % 2 ? source.height : source.width, angle % 2 ? source.width : source.height);
  const context = result.getContext('2d');
  context.translate(result.width / 2, result.height / 2); context.rotate(angle * Math.PI / 2);
  context.drawImage(source, -source.width / 2, -source.height / 2);
  return result;
}

export async function renderPhoto(source, points, brightness, contrast) {
  if (!validCorners(points)) throw new Error('Place the four corners around the card without crossing the edges.');
  const distance = (a, b) => Math.hypot((a.x - b.x) * source.width, (a.y - b.y) * source.height);
  const edgeWidth = Math.min(distance(points[0], points[1]), distance(points[3], points[2]));
  const edgeHeight = Math.min(distance(points[0], points[3]), distance(points[1], points[2]));
  const width = Math.floor(Math.min(900, edgeWidth, edgeHeight * CARD_RATIO));
  if (width < 64) throw new Error('The selected card area is too small. Use a closer, higher-resolution photo.');
  const height = Math.round(width / CARD_RATIO);
  const flattened = flattenPixels(source.getContext('2d').getImageData(0, 0, source.width, source.height), width, height, points);
  const result = canvas(width, height), context = result.getContext('2d');
  const data = context.createImageData(width, height);
  data.data.set(adjustLight(flattened, brightness, contrast)); context.putImageData(data, 0, 0);
  const blob = await new Promise(resolve => result.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not prepare the card photo. Try another image.');
  return { file: new File([blob], 'card-adjusted.png', { type: 'image/png' }), width, height, auto: gentleLight(flattened) };
}
